import React, { useContext, useEffect, useState } from "react";

import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartRichTextEditor from "./smartForm/SmartRichTextEditor";
import SmartImageUpload from './smartForm/SmartImageUpload';
import SmartContainer from './smartForm/SmartContainer';
import { Typography } from '@mui/material';

import { usePostUpdateMutation, usePostDeleteMutation } from "@mutation/postMutation";
import { usePostQuery } from "@query/postQuery";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";
import { removeEmptyValues } from '@utils/generalUtils';

function PostForm({ uuid, onSave, onCancel, onDelete }) {
    const postQuery = usePostQuery(uuid, false);
    const postUpdateMutation = usePostUpdateMutation(uuid);
    const postDeleteMutation = usePostDeleteMutation(uuid);

    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["main", "posts", "error"]);

    const [postModel, setPostModel] = useState();

    useEffect(() => {
        setPostModel(undefined); // Reset the model when uuid changes
    }, [uuid]);

    useEffect(() => {
        if (postQuery.data?.data) {
            const post = postQuery.data.data;
            setPostModel(post);
        }
    }, [postQuery.data]);

    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            heading: newModel.heading,
            body: JSON.stringify(newModel.body),
            file_image_uuid: newModel.file_image.uuid
        });

        postUpdateMutation.mutate({ data: cleaned }, {
            onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success", 4000);
                onSave?.();
            },
            onError: (error) => {
                if (!error.response) {
                    addAlertBanner(t("error:networkError"), "error", 4000);
                    return;
                }

                const statusCode = error.response.status;

                if (statusCode === 403) {
                addAlertBanner(t("error:noModificationPrivileges"), "error", 5000);
                } else if (statusCode === 404) {
                addAlertBanner(t("error:notFound"), "error", 4000);
                } else if (statusCode === 400) {
                addAlertBanner(t("error:validationError"), "error", 5000);
                } else if (statusCode === 422) {
                addAlertBanner(t("error:validationError"), "error", 5000);
                } else if (statusCode >= 500) {
                addAlertBanner(t("error:serverError"), "error", 4000);
                } else {
                addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
                }
            }
        });
    };

    const handleCancel = () => {
        onCancel?.();
    };

    const handleDelete = () => {
        postDeleteMutation.mutate({}, {
            onSuccess: () => {
                addAlertBanner(t("main:deleteSuccess"), "success");
                onDelete?.();
            },
            onError: () => addAlertBanner(t("main:deleteError"), "error")
        });
    };

    return (
        <SmartForm
            formModel={postModel}
            setFormModel={setPostModel}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={handleDelete}
            isLoading={postQuery.isLoading}
            hasShowDeleteButton={true}
            buttonPanelSx={{
                top: "10px",
                right: "10px",
            }}
        >
            <SmartContainer sx={{ flex: 1 }}>
                <SmartImageUpload imageSx={{
                    width:"100%",
                    height:"auto",
                    maxHeight: "300px",
                    mb: 0,
                    borderRadius: 0
                }} field="file_image.uuid" />
            </SmartContainer>

            <SmartContainer sx={{ flex: 1, p: 1}} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <SmartTextInput field="heading" label={t("posts:heading")} renderView={
                    (value) => { return (
                        <Typography variant="h4">
                            {value || '-'}
                        </Typography>
                    );}
                }/>
                <SmartRichTextEditor field="body"/>
            </SmartContainer>
        </SmartForm>
    );
}

export default PostForm;